/**
 * Viewport containment rule for floating surfaces (context menus, dropdowns,
 * popovers): a menu must never overflow the main window. Anchor it at a
 * viewport point, then this hook measures the rendered element and shifts it
 * back inside the window edges before the browser paints (useLayoutEffect),
 * so the adjustment is invisible to the user.
 *
 * Usage: render the menu with `position: fixed` and apply the returned
 * `left`/`top`; attach the returned `ref` to the menu root element.
 */

import { useLayoutEffect, useRef, useState } from 'react';

export const VIEWPORT_MENU_MARGIN = 8;

export interface ViewportPoint {
    x: number;
    y: number;
}

interface UseViewportClampedPositionOptions {
    /** Align the menu's right edge to anchor.x instead of its left edge. */
    align?: 'start' | 'end';
    /** Minimum distance kept between the menu and the window edges. */
    margin?: number;
}

export const useViewportClampedPosition = <T extends HTMLElement>(
    anchor: ViewportPoint | null,
    { align = 'start', margin = VIEWPORT_MENU_MARGIN }: UseViewportClampedPositionOptions = {},
) => {
    const ref = useRef<T | null>(null);
    const [position, setPosition] = useState<ViewportPoint>({
        x: anchor?.x ?? 0,
        y: anchor?.y ?? 0,
    });

    useLayoutEffect(() => {
        if (!anchor) return;
        const rect = ref.current?.getBoundingClientRect();
        const width = rect?.width ?? 0;
        const height = rect?.height ?? 0;
        const maxX = Math.max(margin, window.innerWidth - width - margin);
        const maxY = Math.max(margin, window.innerHeight - height - margin);
        const desiredX = align === 'end' ? anchor.x - width : anchor.x;
        setPosition({
            x: Math.min(Math.max(margin, desiredX), maxX),
            y: Math.min(Math.max(margin, anchor.y), maxY),
        });
        // Re-measure only when the anchor or layout inputs change; the menu
        // element mounts fresh on every open, so its size is measured then.
    }, [anchor?.x, anchor?.y, align, margin]);

    return { ref, left: position.x, top: position.y };
};
